import {
	wrapper,
	createApiClient,
	Result,
	ApiError,
	PaginationParams,
} from "../base";
import DashboardDO = App.Data.DashboardDO;
import { LaravelPaginatedCollection } from "@/types/collections";
import { FileSearchParams } from "@/types/fileSearch";
import FileDTO = App.Data.FileDTO;
import DocumentDO = App.Data.Explorer.DocumentDO;
import SegmentListItemDTO = App.Data.Segments.SegmentListItemDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;
import SharedResourcePermissionDTO = App.Data.SharedResourcePermissionDTO;
import SharedResourcePermissionForRoleDTO = App.Data.SharedResourcePermissionForRoleDTO;
import PublicUserDO = App.Data.PublicUserDO;
import RoleDO = App.Data.RoleDO;
import AbilityName = App.Enums.AbilityName;
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import SingleMetricResponseDTO = App.Data.Explorer.DataTable.SingleMetricResponseDTO;
import ExportFileType = App.Enums.ExportFileType;

const baseClient = createApiClient();

type DimensionParams = PaginationParams & {
	sort?: string;
	"filter[type]"?: string | object;
	"filter[is_filterable]"?: boolean;
	"filter[is_configurable_on_axis]"?: boolean;
};

type DashboardUpsertParams = {
	mode?: string;
};

export default class {
	index(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
	}): Promise<Result<LaravelPaginatedCollection<DashboardDO>, ApiError>> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get("/dashboard", {
				params: {
					"filter[mode]": "print",
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
				},
			})
		);
	}

	get(dashboardUUID: string): Promise<Result<DashboardDO, ApiError>> {
		return wrapper(baseClient.get(`/dashboard/${dashboardUUID}`));
	}

	set(
		dashboardUUID: string,
		data: DashboardDO
	): Promise<Result<DashboardDO, ApiError>> {
		return wrapper(baseClient.put(`/dashboard/${dashboardUUID}`, data));
	}

	createEmptyStoredDocument(
		storedDocument: App.Data.StoredDocumentDO
	): Promise<Result<App.Data.FileDTO, ApiError>> {
		return wrapper(
			baseClient.post(`dashboard/create-empty-stored-document`, storedDocument)
		);
	}

	getDashboardFromHash(
		dashboardExportUUID: string
	): Promise<Result<DashboardDO, ApiError>> {
		return wrapper(
			baseClient.get(`/gotenberg/get-dashboard-export/${dashboardExportUUID}`)
		);
	}

	downloadDashboard(dashboard: DashboardDO): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.get(`/dashboard/generate-pdf/${dashboard.uuid}`, {
				responseType: "blob",
				timeout: 60000,
			})
		);
	}

	/**
	 * Create a new dashboard
	 * @param dashboard Dashboard data
	 * @param mode Optional dashboard mode (web or print)
	 */
	createDashboard(
		dashboard: FileDTO
	): Promise<Result<App.Data.FileDTO, ApiError>> {
		return wrapper(baseClient.post(`/dashboard`, dashboard));
	}

	removeDashboard(dashboard: App.Data.FileDTO): Promise<Result<[], ApiError>> {
		return wrapper(baseClient.delete(`/dashboard/${dashboard.uuid}`));
	}

	batchRemoveDashboards(
		dashboards: App.Data.FileDTO[]
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.delete(`/dashboard`, {
				data: { ids: dashboards.map((dashboard) => dashboard.id) },
			})
		);
	}
	/**
	 * Update dashboard metadata
	 * @param dashboard Dashboard data
	 * @param mode Optional dashboard mode (web or print)
	 */
	updateDashboardFromFinder(
		dashboard: FileDTO,
		params: DashboardUpsertParams = {}
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`/dashboard/update-from-finder/${dashboard.uuid}`,
				dashboard,
				{ params }
			)
		);
	}

	getDimensions(
		params: DimensionParams = {}
	): Promise<Result<App.Data.DimensionDO[], ApiError>> {
		return wrapper(baseClient.get(`dashboard/get-dimensions`, { params }));
	}

	getHierarchy(
		documentDO: DocumentDO | null = null,
		hierarchy_definition_id: number,
		excludedBranches: object = {},
		includeVerticalValues: boolean = false,
		signal?: AbortSignal
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(
				`dashboard/get-hierarchy/${hierarchy_definition_id}`,
				{
					document: documentDO,
					excludedBranches,
					includeVerticalValues,
				},
				{ signal }
			)
		);
	}

	getValuesForDimensionAndSpecification(
		dimension_id: number,
		specification_id: number | null
	): Promise<Result<App.Data.Explorer.Filter.FilterValueDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`dashboard/get-values-for-dimension-and-specification/${dimension_id}` +
					(specification_id ? "/" + specification_id : "")
			)
		);
	}

	getBuckets(): Promise<Result<App.Data.DimensionBucketDO[], ApiError>> {
		return wrapper(baseClient.get(`dashboard/get-buckets`));
	}

	indexSegments(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
	}): Promise<
		Result<LaravelPaginatedCollection<SegmentListItemDTO>, ApiError>
	> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get(`dashboard/index-segments`, {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
				},
			})
		);
	}

	searchDashboards(
		params: FileSearchParams = {}
	): Promise<Result<LaravelPaginatedCollection<FileDTO>, ApiError>> {
		return wrapper(baseClient.get(`dashboard/search`, { params }));
	}

	getOwnedDashboardsByResourceTreeNode(
		resourceTreeNode: ResourceTreeNodeDO
	): Promise<Result<App.Data.FileDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`dashboard/get-owned-dashboards-by-resource-tree-node/${resourceTreeNode.uuid}`
			)
		);
	}

	getDashboardsSharedWithMe(): Promise<Result<App.Data.FileDTO[], ApiError>> {
		return wrapper(baseClient.get(`dashboard/get-dashboards-shared-with-me`));
	}

	getUsersWithPermissionsToDashboard(
		dashboard: FileDTO
	): Promise<Result<SharedResourcePermissionDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`dashboard/get-users-with-permissions-to/${dashboard.uuid}`
			)
		);
	}

	grantPermissionsToDashboardForUser(
		storedDocument: FileDTO,
		user: PublicUserDO,
		ability: AbilityName
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`dashboard/grant-permissions-to-dashboard-for-user/${user.id}/${ability}`,
				storedDocument
			)
		);
	}

	revokePermissionsToDashboardForUser(
		storedDocument: FileDTO,
		user: PublicUserDO
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`dashboard/revoke-permissions-to-dashboard-for-user/${user.id}`,
				storedDocument
			)
		);
	}

	getRolesWithPermissionsToDashboard(
		dashboard: FileDTO
	): Promise<Result<SharedResourcePermissionForRoleDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`dashboard/get-roles-with-permissions-to/${dashboard.uuid}`
			)
		);
	}

	grantPermissionsToDashboardForRole(
		storedDocument: FileDTO,
		role: RoleDO,
		ability: AbilityName
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`dashboard/grant-permissions-to-dashboard-for-role/${role.id}/${ability}`,
				storedDocument
			)
		);
	}

	revokePermissionsToDashboardForRole(
		storedDocument: FileDTO,
		role: RoleDO
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`dashboard/revoke-permissions-to-dashboard-for-role/${role.id}`,
				storedDocument
			)
		);
	}

	fetchDataForDocument(
		dashboardDO: DashboardDO,
		cardUuid: string,
		signal?: AbortSignal
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(
				`dashboard/fetchDataForDocument`,
				{ dashboardDO, cardUuid },
				{
					signal,
				}
			)
		);
	}

	getNormalizedDocument(
		dashboardDO: DashboardDO,
		cardUuid: string
	): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(
			baseClient.post(`dashboard/get-normalized-document`, {
				dashboardDO,
				cardUuid,
			})
		);
	}

	fetchTotalsForDocumentDataTable(
		dashboardDO: DashboardDO,
		cardUuid: string,
		signal?: AbortSignal
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(
				`dashboard/fetchTotalsForDocumentDataTable`,
				{ dashboardDO, cardUuid },
				{ signal }
			)
		);
	}

	fetchDataForDocumentDataTable(
		dashboardDO: DashboardDO,
		cardUuid: string,
		signal?: AbortSignal
	): Promise<Result<{ data: DataTableResponseDTO[] }, ApiError>> {
		return wrapper(
			baseClient.post(
				`dashboard/fetchDataForDocumentDataTable`,
				{ dashboardDO, cardUuid },
				{ signal }
			)
		);
	}

	fetchDataForDocumentSingleMetric(
		dashboardDO: DashboardDO,
		cardUuid: string,
		signal?: AbortSignal,
		useTargetDocument = false
	): Promise<Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>> {
		return wrapper(
			baseClient.post(
				`dashboard/fetchDataForDocumentSingleMetric`,
				{ dashboardDO, cardUuid, useTargetDocument },
				{ signal }
			)
		).then(
			(result: Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>) => {
				if (result.isOk()) {
					if (result.value.singleMetric) {
						if (typeof result.value.singleMetric?.data === "number") {
							return result;
						}

						result.value.singleMetric.data =
							result.value.singleMetric?.data === "-Inf"
								? -Infinity
								: +Infinity;
					}
				}
				return result;
			}
		);
	}
	downloadDataTable(
		dashboardDO: DashboardDO,
		cardUuid: string,
		format: ExportFileType
	) {
		return wrapper(
			baseClient.post(
				`dashboard/download-data-table`,
				{ dashboardDO, cardUuid },
				{
					responseType: "blob",
					params: { format },
				}
			)
		);
	}
}
